/**
 * convPrefs slice — per-conversation client-side preferences.
 *
 * Tracks three orthogonal sets of conversation IDs that the user has
 * marked locally (no backend round-trip):
 *   - pinned    → rendered at the top of the chat list
 *   - archived  → hidden from the main list (visible via the Archived filter)
 *   - muted     → desktop notifications + receive chime suppressed; unread
 *                 badge still shows so the user can see new activity
 *
 * State is persisted to localStorage so preferences survive app restarts.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const STORAGE_KEY = 'gc.convPrefs.v1';

export interface ConvPrefsState {
  pinned: string[];
  archived: string[];
  muted: string[];
}

const defaults: ConvPrefsState = { pinned: [], archived: [], muted: [] };

function loadInitial(): ConvPrefsState {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    const sanitize = (v: unknown): string[] =>
      Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    return {
      pinned: sanitize(parsed.pinned),
      archived: sanitize(parsed.archived),
      muted: sanitize(parsed.muted),
    };
  } catch {
    return defaults;
  }
}

function persist(state: ConvPrefsState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* quota / private mode — ignore */
  }
}

function toggle(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter(x => x !== id) : [...list, id];
}

const slice = createSlice({
  name: 'convPrefs',
  initialState: loadInitial(),
  reducers: {
    togglePin(state, action: PayloadAction<string>) {
      state.pinned = toggle(state.pinned, action.payload);
      // Unarchive when pinning — the two are mutually exclusive in the UI.
      if (state.pinned.includes(action.payload)) {
        state.archived = state.archived.filter(x => x !== action.payload);
      }
      persist(state);
    },
    toggleArchive(state, action: PayloadAction<string>) {
      state.archived = toggle(state.archived, action.payload);
      // Unpinning when archiving keeps pin/archive mutually exclusive.
      if (state.archived.includes(action.payload)) {
        state.pinned = state.pinned.filter(x => x !== action.payload);
      }
      persist(state);
    },
    toggleMute(state, action: PayloadAction<string>) {
      state.muted = toggle(state.muted, action.payload);
      persist(state);
    },
    /** Remove a conversation from every list — call after deletion so we
     *  don't leak preferences for IDs the server no longer knows about. */
    forgetConversation(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.pinned = state.pinned.filter(x => x !== id);
      state.archived = state.archived.filter(x => x !== id);
      state.muted = state.muted.filter(x => x !== id);
      persist(state);
    },
  },
});

export const { togglePin, toggleArchive, toggleMute, forgetConversation } =
  slice.actions;

export default slice.reducer;

type RootShape = { convPrefs: ConvPrefsState };

export const selectPinned = (s: RootShape): string[] => s.convPrefs.pinned;
export const selectArchived = (s: RootShape): string[] => s.convPrefs.archived;
export const selectMuted = (s: RootShape): string[] => s.convPrefs.muted;

export const selectIsPinned = (s: RootShape, id: string): boolean =>
  s.convPrefs.pinned.includes(id);
export const selectIsArchived = (s: RootShape, id: string): boolean =>
  s.convPrefs.archived.includes(id);
export const selectIsMuted = (s: RootShape, id: string): boolean =>
  s.convPrefs.muted.includes(id);
